import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { MdCheck, MdDeleteOutline, MdFileUpload, MdPrint, MdRestartAlt } from "react-icons/md";

import { useActiveBusiness } from "../auth/useActiveBusiness";
import { bundledLogoFor } from "../businessBranding";
import { useBranding } from "../theme/BrandingProvider";
import { fetchBranding, updateBranding, resetBranding } from "../theme/brandingApi";
import {
  PRESETS,
  presetById,
  tokensForTheme,
  cssVarsFor,
  normHex,
  contrast,
} from "../theme/palette";
import "../settings-branding.css";

/* ------------------------------------------------------------------ *
 * Settings -> Branding
 *
 * The open business's colour palette and logo. Both apply to every page,
 * modal and print page: the palette through the --rf-* custom properties
 * ThemeRoot writes to :root, the logo through brandFor().
 *
 * Edits are a draft shown in the live preview on the right; nothing
 * outside the preview changes until Save. Reads and writes go through
 * ../theme/brandingApi.js — stored per business on the server, so every
 * device and person sees the same palette and logo.
 * ------------------------------------------------------------------ */

const SEEDS = [
  { key: "primary", label: "Primary", help: "Buttons, links, active states, print headings" },
  { key: "accent", label: "Accent", help: "Second chart series, secondary chips" },
  { key: "header", label: "Header band", help: "The top bar behind the logo and name" },
];

/* An upload is stored in the branding doc as a data URL, so keep it small:
   raster images are scaled down to at most this box before saving. */
const MAX_LOGO_W = 960;
const MAX_LOGO_H = 240;
const MAX_INPUT_BYTES = 5 * 1024 * 1024;
const MAX_SVG_BYTES = 300 * 1024;

const sameDoc = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* the seed colours a theme actually resolves to */
function seedsOf(theme) {
  const preset = presetById(theme.preset) || PRESETS[0];
  return {
    primary: normHex(theme.primary) || preset.primary,
    accent: normHex(theme.accent) || preset.accent,
    header: normHex(theme.header) || preset.header,
  };
}

/* which preset (if any) a theme is exactly */
function matchingPreset(theme) {
  const s = seedsOf(theme);
  return (
    PRESETS.find((p) => p.primary === s.primary && p.accent === s.accent && p.header === s.header) ||
    null
  );
}

function readLogoFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file chosen."));
    if (!/^image\/(png|jpe?g|webp|svg\+xml|gif)$/.test(file.type)) {
      return reject(new Error("Use a PNG, JPG, WebP, GIF or SVG image."));
    }
    if (file.size > MAX_INPUT_BYTES) {
      return reject(new Error("That image is over 5 MB — export a smaller one."));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file."));
    reader.onload = () => {
      const dataUrl = reader.result;
      if (file.type === "image/svg+xml") {
        if (file.size > MAX_SVG_BYTES) {
          return reject(new Error("That SVG is over 300 KB — simplify or export it as PNG."));
        }
        return resolve(dataUrl);
      }
      // scale raster artwork down to a logo-sized box
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a readable image."));
      img.onload = () => {
        const scale = Math.min(1, MAX_LOGO_W / img.width, MAX_LOGO_H / img.height);
        if (scale === 1 && file.size < 250 * 1024) return resolve(dataUrl);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/* ------------------------------------------------------------------ */

export function BrandingSection({ canManage }) {
  const business = useActiveBusiness();
  const saved = useBranding(business);
  const savedDoc = useMemo(() => ({ theme: saved.theme, logo: saved.logo }), [saved]);

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoError, setLogoError] = useState("");
  const fileRef = useRef(null);

  /* load once per business; a save elsewhere (another tab) doesn't stomp
     an open draft — only a clean draft follows the stored copy */
  const businessKey = business ? business.id : null;
  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchBranding(business).then((res) => {
      if (!live) return;
      setDraft(res.data.response);
      setLoading(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessKey]);

  const dirty = !!draft && !sameDoc(draft, savedDoc);

  const tokens = useMemo(() => tokensForTheme((draft || savedDoc).theme), [draft, savedDoc]);

  if (!business) {
    return (
      <div className="st-card">
        <div className="st-state">
          <b>No business is open.</b>
          Open a business from the group overview to set its branding.
        </div>
      </div>
    );
  }

  if (loading || !draft) {
    return (
      <div className="st-card">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="st-skel" key={i} />
        ))}
      </div>
    );
  }

  const seeds = seedsOf(draft.theme);
  const exactPreset = matchingPreset(draft.theme);
  const businessName = business.name || business.code || "Business";
  const bundled = bundledLogoFor(business);
  const logoSrc = draft.logo.url || bundled;

  const setTheme = (theme) => setDraft((d) => ({ ...d, theme }));
  const setLogo = (patch) => setDraft((d) => ({ ...d, logo: { ...d.logo, ...patch } }));

  function pickPreset(p) {
    setTheme({ preset: p.id, primary: null, accent: null, header: null });
  }

  function setSeed(key, value) {
    const hex = normHex(value);
    if (!hex) return;
    const next = { ...seeds, [key]: hex };
    const exact = PRESETS.find(
      (p) => p.primary === next.primary && p.accent === next.accent && p.header === next.header
    );
    setTheme(
      exact
        ? { preset: exact.id, primary: null, accent: null, header: null }
        : { preset: "custom", ...next }
    );
  }

  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    setLogoError("");
    try {
      const url = await readLogoFile(file);
      setLogo({ url });
    } catch (err) {
      setLogoError(err.message);
    }
  }

  function save() {
    if (!canManage) {
      toast.warn("You don't have permission to change settings.");
      return;
    }
    setSaving(true);
    updateBranding(business, draft).then((res) => {
      setSaving(false);
      if (res.data.status !== "success") {
        toast.error(res.data.message || "Couldn't save branding.");
        return;
      }
      setDraft(res.data.response);
      toast.success("Branding saved — every page now uses it.");
    });
  }

  function resetToDefault() {
    if (!canManage) return;
    setSaving(true);
    resetBranding(business).then((res) => {
      setSaving(false);
      setDraft(res.data.response);
      toast.success("Branding reset to the workspace default.");
    });
  }

  /* legibility checks on the draft */
  const warnings = [];
  if (contrast(seeds.primary, "#ffffff") < 3) {
    warnings.push("White text on primary buttons will be hard to read — pick a darker primary.");
  }
  if (contrast(seeds.header, "#ffffff") < 3) {
    warnings.push("The header's white business name, icons and clock will be hard to read — pick a darker header band.");
  }

  const previewVars = {
    ...cssVarsFor(tokens),
    "--rf-logo-h": `${draft.logo.height || 36}px`,
    "--rf-logo-print-h": `${draft.logo.print_height || 40}px`,
  };

  return (
    <div className="sb-layout">
      <div className="sb-controls">
        {/* ---------- palettes ---------- */}
        <div className="st-card">
          <div className="st-card-head">
            <span className="st-card-title">Colour palette</span>
            <span className="st-card-meta">
              {exactPreset ? exactPreset.name : "Custom"}
            </span>
          </div>
          <p className="st-card-help">
            Pick a starting palette. Every shade the app uses — hovers, tints,
            hairlines, print accents — is worked out from these colours.
          </p>
          <div className="sb-presets" role="radiogroup" aria-label="Colour palette">
            {PRESETS.map((p) => {
              const on = exactPreset && exactPreset.id === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={!!on}
                  className={`sb-preset${on ? " is-on" : ""}`}
                  disabled={!canManage}
                  onClick={() => pickPreset(p)}
                >
                  <span className="sb-preset-swatch" aria-hidden="true">
                    <i style={{ background: p.header }} />
                    <i style={{ background: p.primary }} />
                    <i style={{ background: p.accent }} />
                  </span>
                  <span className="sb-preset-name">{p.name}</span>
                  {on && <MdCheck className="sb-preset-check" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- custom colours ---------- */}
        <div className="st-card">
          <div className="st-card-head">
            <span className="st-card-title">Custom colours</span>
          </div>
          <p className="st-card-help">
            Fine-tune any of the three. Changing one turns the palette into a
            custom one.
          </p>
          <div className="sb-seeds">
            {SEEDS.map((s) => (
              <SeedField
                key={s.key}
                label={s.label}
                help={s.help}
                value={seeds[s.key]}
                disabled={!canManage}
                onChange={(v) => setSeed(s.key, v)}
              />
            ))}
          </div>
          {warnings.length > 0 && (
            <ul className="sb-warn">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>

        {/* ---------- logo ---------- */}
        <div className="st-card">
          <div className="st-card-head">
            <span className="st-card-title">Logo</span>
            <span className="st-card-meta">
              {draft.logo.url ? "Uploaded" : bundled ? "Built-in artwork" : "Monogram"}
            </span>
          </div>
          <p className="st-card-help">
            Shown in the top bar, on the group overview, in order history and on
            every printed sheet. A wide PNG or SVG with a transparent background
            works best.
          </p>

          <div className="sb-logo-row">
            <div className="sb-logo-tile is-cream" style={previewVars}>
              <LogoMark src={logoSrc} name={businessName} code={business.code} />
            </div>
            <div className="sb-logo-tile is-paper">
              <LogoMark src={logoSrc} name={businessName} code={business.code} />
            </div>
          </div>

          <div className="sb-logo-actions">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              hidden
              onChange={onFile}
            />
            <button
              type="button"
              className="rf-btn rf-btn-ghost st-btn-sm"
              disabled={!canManage}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              <MdFileUpload style={{ verticalAlign: "-3px" }} />{" "}
              {draft.logo.url ? "Replace logo" : "Upload logo"}
            </button>
            {draft.logo.url && (
              <button
                type="button"
                className="rf-btn rf-btn-ghost st-btn-sm"
                disabled={!canManage}
                onClick={() => setLogo({ url: null })}
              >
                <MdDeleteOutline style={{ verticalAlign: "-3px" }} />{" "}
                {bundled ? "Use built-in artwork" : "Remove"}
              </button>
            )}
          </div>
          {logoError && <p className="sb-error">{logoError}</p>}

          <div className="sb-sliders">
            <RangeField
              label="Size in the top bar"
              min={24}
              max={56}
              value={draft.logo.height || 36}
              disabled={!canManage}
              onChange={(v) => setLogo({ height: v })}
            />
            <RangeField
              label="Size on printed sheets"
              min={28}
              max={72}
              value={draft.logo.print_height || 40}
              disabled={!canManage}
              onChange={(v) => setLogo({ print_height: v })}
            />
          </div>
        </div>
      </div>

      {/* ---------- live preview ---------- */}
      <div className="sb-side">
        <div className="sb-preview-label">Preview</div>
        <Preview
          vars={previewVars}
          logoSrc={logoSrc}
          businessName={businessName}
          code={business.code}
        />

        <div className={`sb-savebar${dirty ? " is-dirty" : ""}`}>
          <span className="sb-savebar-note">
            {dirty
              ? "Unsaved changes — only the preview shows them."
              : saved.isCustomised
              ? "Saved. Every page, modal and print uses this branding."
              : "Using this workspace's default branding."}
          </span>
          <div className="sb-savebar-actions">
            {saved.isCustomised && !dirty && canManage && (
              <button
                type="button"
                className="rf-btn rf-btn-ghost st-btn-sm"
                onClick={resetToDefault}
                disabled={saving}
                title="Drop the saved branding and go back to the workspace default"
              >
                <MdRestartAlt style={{ verticalAlign: "-3px" }} /> Reset
              </button>
            )}
            {dirty && (
              <button
                type="button"
                className="rf-btn rf-btn-ghost st-btn-sm"
                onClick={() => setDraft(savedDoc)}
                disabled={saving}
              >
                Discard
              </button>
            )}
            <button
              type="button"
              className="rf-btn rf-btn-primary st-btn-sm"
              onClick={save}
              disabled={!dirty || saving || !canManage}
            >
              {saving ? "Saving…" : "Save branding"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- pieces ---------- */

function SeedField({ label, help, value, disabled, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    const hex = normHex(text.startsWith("#") ? text : `#${text}`);
    if (hex) onChange(hex);
    else setText(value);
  };
  return (
    <div className="sb-seed">
      <label className="sb-seed-chip" style={{ background: value }}>
        <input
          type="color"
          value={value}
          disabled={disabled}
          aria-label={`${label} colour`}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <div className="sb-seed-main">
        <div className="sb-seed-label">{label}</div>
        <div className="sb-seed-help">{help}</div>
      </div>
      <input
        className="st-input sb-seed-hex"
        value={text}
        disabled={disabled}
        spellCheck={false}
        maxLength={7}
        aria-label={`${label} hex value`}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setText(value);
        }}
      />
    </div>
  );
}

function RangeField({ label, min, max, value, disabled, onChange }) {
  return (
    <label className="sb-range">
      <span className="sb-range-label">
        {label}
        <b className="rf-num">{value}px</b>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function monogram(name, code) {
  if (code) return String(code).toUpperCase().slice(0, 3);
  return (
    String(name || "")
      .split(/\s+/)
      .filter((w) => w && !/^(the|and|of)$/i.test(w))
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "??"
  );
}

function LogoMark({ src, name, code, print }) {
  if (src) return <img src={src} alt="" className={print ? "sb-print-logo" : "sb-logo-img"} />;
  return <span className="sb-monogram">{monogram(name, code)}</span>;
}

/* A miniature of the app chrome, a card, a modal and a printed slip, all
   painted from the draft tokens (set as custom properties on this box, so
   they override :root inside it only). */
function Preview({ vars, logoSrc, businessName, code }) {
  return (
    <div className="sb-preview" style={vars}>
      <div className="sb-pv-app">
        <div className="sb-pv-top">
          <span className="sb-pv-burger" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="sb-pv-logo">
            <LogoMark src={logoSrc} name={businessName} code={code} />
          </span>
          <span className="sb-pv-name">{businessName}</span>
          <span className="rf-spacer" />
          <span className="sb-pv-search" />
        </div>
        <div className="sb-pv-row">
          <div className="sb-pv-side">
            <span className="sb-pv-nav is-active">
              <i /> Work orders <b>12</b>
            </span>
            <span className="sb-pv-nav">
              <i /> Delivery runs
            </span>
            <span className="sb-pv-nav">
              <i /> Products
            </span>
          </div>
          <div className="sb-pv-main">
            <div className="sb-pv-title">Today</div>
            <div className="sb-pv-chips">
              <span className="sb-pv-chip">Funeral</span>
              <span className="sb-pv-chip is-accent">Retail</span>
              <span className="sb-pv-link">View all</span>
            </div>
            <div className="sb-pv-bars" aria-hidden="true">
              {[62, 80, 45, 92, 70, 55].map((h, i) => (
                <span key={i}>
                  <i style={{ height: `${h}%` }} />
                  <i className="is-accent" style={{ height: `${h * 0.55}%` }} />
                </span>
              ))}
            </div>
            <div className="sb-pv-modal">
              <div className="sb-pv-modal-title">Mark order complete?</div>
              <div className="sb-pv-modal-actions">
                <span className="sb-pv-btn is-ghost">Cancel</span>
                <span className="sb-pv-btn">Complete</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sb-pv-print">
        <div className="sb-pv-print-head">
          <LogoMark src={logoSrc} name={businessName} code={code} print />
          <div className="sb-pv-print-meta">
            <span className="sb-pv-print-kind">
              <MdPrint /> Work order
            </span>
            <b className="rf-num">#2417</b>
          </div>
        </div>
        <div className="sb-pv-print-rule" />
        <div className="sb-pv-print-lines">
          <span />
          <span />
          <span className="is-short" />
        </div>
      </div>
    </div>
  );
}

export default BrandingSection;
